// see https://github.com/orgs/vuejs/discussions/11798
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { type RouteRecordRaw } from 'vue-router';
import { Navigation } from '@/router/navigation';
import { requireLoggedIn } from '@/router/guards/require-logged-in';
import { initNavAreaMyArea } from '@/router/guards/init-nav-area';
import { requireRetailer } from '@/router/guards/require-retailer';
import { preventCrawlerIndex } from './guards/prevent-crawler-index';
import { setNavAreaSwitchEnd, setNavAreaSwitchStart } from './guards/set-nav-area-switch';
import { requireCustomer } from './guards/require-customer';
import { requirePermission } from '@/router/guards/require-permission.ts';
import { PermissionName } from '@containex/portal-backend-dto';

export const accountRoutes: RouteRecordRaw = {
    path: 'account',
    component: async () => import('../account/views/AccountRouterView.vue'),
    beforeEnter: [setNavAreaSwitchStart, requireLoggedIn, initNavAreaMyArea, setNavAreaSwitchEnd],
    children: [
        {
            path: '',
            name: Navigation.Account,
            component: async () => import('../account/views/AccountOverviewView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT',
            },
        },
        {
            path: 'settings',
            name: Navigation.AccountSettings,
            component: async () => import('../account/views/AccountSettingsView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_SETTINGS',
            },
            beforeEnter: [],
        },
        {
            path: 'users',
            name: Navigation.Users,
            component: async () => import('../account/views/UserListView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_USERS',
            },
            beforeEnter: [requirePermission(PermissionName.USERS_VIEW)],
        },
        {
            path: 'users/edit/:id',
            props: (route) => ({
                id: route.params.id,
            }),
            name: Navigation.UsersEdit,
            component: async () => import('../account/views/UserEditView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_USERS',
            },
            beforeEnter: [requirePermission(PermissionName.USERS_MANAGE)],
        },
        {
            path: 'employees',
            name: Navigation.Employees,
            component: async () => import('../account/views/EmployeesView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_EMPLOYEES',
            },
            beforeEnter: [requirePermission(PermissionName.EMPLOYEES_VIEW)],
        },
        {
            path: 'orders',
            name: Navigation.OrderList,
            component: async () => import('../account/views/OrderListView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_ORDERS',
            },
            beforeEnter: [requireCustomer, requirePermission(PermissionName.ORDERS_VIEW)],
        },
        // For backward compatibility, redirect old order detail route to the new one
        {
            path: 'orders/:id',
            redirect: (to) => ({
                ...to,
                name: Navigation.OrderDetail,
            }),
        },
        {
            path: 'orders/detail/:id',
            name: Navigation.OrderDetail,
            props: true,
            component: async () => import('../account/views/OrderDetailView.vue'),
            beforeEnter: [requireCustomer, requirePermission(PermissionName.ORDERS_VIEW)],
        },
        {
            path: 'order-confirmations',
            name: Navigation.OrderConfirmationList,
            component: async () => import('../account/views/OrderConfirmationsListView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_ORDER_CONFIRMATIONS',
            },
            beforeEnter: [requirePermission(PermissionName.ORDERCONFIRMATIONS_VIEW)],
        },
        // For backward compatibility, redirect old order confirmation detail route to the new one
        {
            path: 'order-confirmations/:id',
            redirect: (to) => ({
                ...to,
                name: Navigation.OrderConfirmationDetail,
            }),
        },
        {
            path: 'order-confirmations/detail/:id',
            name: Navigation.OrderConfirmationDetail,
            props: true,
            component: async () => import('../account/views/OrderConfirmationDetailView.vue'),
            beforeEnter: [requirePermission(PermissionName.ORDERCONFIRMATIONS_VIEW)],
        },
        {
            path: 'offers',
            name: Navigation.OfferList,
            component: async () => import('../account/views/OfferListView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_OFFERS',
            },
            beforeEnter: [requireCustomer, requirePermission(PermissionName.OFFERS_VIEW)],
        },
        // For backward compatibility, redirect old offer detail route to the new one
        {
            path: 'offers/:id',
            redirect: (to) => ({
                ...to,
                name: Navigation.OfferDetail,
            }),
        },
        {
            path: 'offers/:id/change-request-history',
            redirect: (to) => ({
                ...to,
                name: Navigation.OfferChangeRequestHistory,
            }),
        },
        {
            path: 'offers/detail/:id',
            name: Navigation.OfferDetail,
            props: true,
            component: async () => import('../account/views/OfferDetailView.vue'),
            beforeEnter: [requireCustomer, requirePermission(PermissionName.OFFERS_VIEW)],
        },
        {
            path: 'offers/detail/:id/change-request-history',
            name: Navigation.OfferChangeRequestHistory,
            props: true,
            component: async () => import('../account/views/ChangeRequestsHistory.vue'),
            beforeEnter: [requireCustomer, requirePermission(PermissionName.OFFERS_VIEW)],
        },
        {
            path: 'account/offers/:id/checkout/success',
            name: Navigation.OfferDetailCheckoutSuccess,
            component: async () => import('../checkout/views/OfferToOrderCheckoutSuccessView.vue'),
            meta: {
                titleKey: 'TITLES.OFFER_TO_ORDER_CHECKOUT_SUCCESS',
            },
            beforeEnter: [requireCustomer, requirePermission(PermissionName.OFFERS_VIEW)],
        },
        {
            path: 'invoices',
            name: Navigation.InvoiceList,
            component: async () => import('../account/views/InvoiceListView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_INVOICES',
            },
            beforeEnter: [requireCustomer, requirePermission(PermissionName.INVOICES_VIEW)],
        },
        {
            path: 'leads',
            name: Navigation.Leads,
            component: async () => import('../account/views/LeadsView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_LEADS',
            },
            beforeEnter: [requireRetailer, requirePermission(PermissionName.LEADS_VIEW)],
        },
        {
            path: 'leads/:id',
            props: (route) => ({
                id: route.params.id,
            }),
            name: Navigation.LeadDetails,
            component: async () => import('../account/views/LeadDetailView.vue'),
            beforeEnter: [requireRetailer, requirePermission(PermissionName.LEADS_VIEW)],
        },
        {
            path: 'downloads',
            name: Navigation.Downloads,
            component: async () => import('../account/views/DownloadsView.vue'),
            meta: {
                titleKey: 'TITLES.ACCOUNT_DOWNLOADS',
            },
            beforeEnter: [requirePermission(PermissionName.DOWNLOADS_VIEW)],
        },
        {
            name: Navigation.AccountNotFound,
            path: 'not-found',
            component: async () => import('../views/NotFoundView.vue'),
            props: () => ({
                redirectRouteName: Navigation.SalesProductList,
            }),
            beforeEnter: [preventCrawlerIndex],
        },
        {
            path: ':pathMatch(.*)*',
            redirect: (to) => ({
                ...to,
                name: Navigation.AccountNotFound,
            }),
            beforeEnter: [preventCrawlerIndex],
        },
    ],
};
